import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { PassChangeReason } from "../models/PassChangeReason";

interface FormErrors {
	Name?: string;
}

interface PassChangeReasonDetailViewerProps {
	reason: PassChangeReason | null;
	existingReasons: PassChangeReason[];
	isSaving: boolean;
	onSave: (reason: PassChangeReason) => void;
	onClose: () => void;
}

export const PassChangeReasonDetailViewer = ({
	reason,
	existingReasons,
	isSaving,
	onSave,
	onClose,
}: PassChangeReasonDetailViewerProps) => {
	const [formData, setFormData] = useState<PassChangeReason | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewReason = !reason?.Id;

	// Validate form and return errors
	const validateForm = useMemo(() => {
		return (data: PassChangeReason | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				// Check for duplicate name (case-insensitive)
				const isDuplicate = existingReasons.some(
					(r) =>
						r.Id !== data.Id &&
						r.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingReasons]);

	// Check if form is valid
	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;

	// Check if new reason can be created
	const canCreateReason = isNewReason && isFormValid;

	useEffect(() => {
		if (reason) {
			setFormData(sanitizeNullStrings({ ...reason }));
		} else {
			setFormData(null);
		}
		// Reset validation state when reason changes
		setErrors({});
		setTouched({});
	}, [reason]);

	if (!reason) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a pass change reason to view</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	// Handle field change (updates local state)
	const handleChange = (field: keyof PassChangeReason, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	// Mark field as touched and show error if any
	const handleBlur = (field: keyof PassChangeReason) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		// Update errors for touched field
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	// Auto-save for existing reasons (called on blur for text fields)
	const handleBlurSave = (field: keyof PassChangeReason, value: unknown) => {
		handleBlur(field);

		if (isNewReason) return; // Don't auto-save new reasons

		// Check for validation errors on the field
		const currentErrors = validateForm(
			formData ? { ...formData, [field]: value } : null,
		);
		if (currentErrors[field as keyof FormErrors]) {
			return; // Don't save if there are validation errors
		}

		// Check if value actually changed
		const originalValue = reason[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			const updatedReason = { ...formData, [field]: value };
			onSave(updatedReason);
		}
	};

	// Auto-save for checkboxes (immediate on change)
	const handleCheckboxChange = (
		field: keyof PassChangeReason,
		value: unknown,
	) => {
		handleChange(field, value);

		if (isNewReason) return; // Don't auto-save new reasons

		// Check if value actually changed
		if (reason[field] !== value) {
			const updatedReason = { ...formData, [field]: value };
			onSave(updatedReason);
		}
	};

	// Create new reason (explicit save)
	const handleCreate = () => {
		// Mark all fields as touched to show errors
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreateReason) {
			onSave(formData);
		}
	};

	const sectionHeaderSx = {
		color: "primary.main",
		fontWeight: 600,
		fontSize: "0.85rem",
		mt: 1.5,
		mb: 0.5,
	};

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{isNewReason ? "New Pass Change Reason" : formData.Name}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					{/* CREATE button only for new reasons */}
					{isNewReason && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreateReason || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)}
					{/* CLOSE button always visible */}
					<Tooltip title="Close">
						<IconButton size="small" onClick={onClose} disabled={isSaving}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>

			<Divider sx={{ mb: 1 }} />

			{/* Form Fields */}
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Basic Information</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Name"
						value={formData.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						onBlur={(e) => handleBlurSave("Name", e.target.value)}
						fullWidth
						size="small"
						required
						error={touched.Name && !!errors.Name}
						helperText={touched.Name && errors.Name}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Description"
						value={formData.Description ?? ""}
						onChange={(e) => handleChange("Description", e.target.value)}
						onBlur={(e) => handleBlurSave("Description", e.target.value)}
						fullWidth
						size="small"
						multiline
						rows={3}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formData.Active}
								onChange={(e) =>
									handleCheckboxChange("Active", e.target.checked)
								}
								size="small"
							/>
						}
						label="Active"
						sx={{ ml: 0 }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
